import ExcelJS from 'exceljs';

interface ExcelColumn {
  header: string;
  key: string;
  width: number;
}

const getExcelColumns = (reportType: string): ExcelColumn[] => {
  if (reportType === 'Monthly Organizer Activity Report') {
    return [
      { header: 'Organizer', key: 'organizerName', width: 25 },
      { header: 'Jobsite', key: 'jobSiteName', width: 30 },
      { header: 'Province', key: 'province', width: 15 },
      { header: 'Union Status', key: 'unionType', width: 15 },
      { header: 'General Contractor', key: 'contractorName', width: 25 },
      { header: 'Creation Date', key: 'startDate', width: 15 },
      { header: 'Notes/Comments', key: 'notes', width: 40 }
    ];
  } else if (reportType === 'Jobsite List Report by Organizer') {
    return [
      { header: 'Organizer Name', key: 'organizerName', width: 25 },
      { header: 'Jobsite Name', key: 'jobSiteName', width: 30 },
      { header: 'Jobsite Status', key: 'status', width: 15 },
      { header: 'Province', key: 'province', width: 15 },
      { header: 'Union Type', key: 'unionType', width: 15 },
      { header: 'Contractor Name', key: 'contractorName', width: 25 },
      { header: 'Start Date', key: 'startDate', width: 15 },
      { header: 'End Date', key: 'endDate', width: 15 },
      { header: 'Last Updated', key: 'lastUpdated', width: 15 }
    ];
  } else if (reportType === 'Jobsite List by Union') {
    return [
      { header: 'Jobsite Name', key: 'jobSiteName', width: 30 },
      { header: 'Sector', key: 'sector', width: 15 },
      { header: 'Province', key: 'province', width: 15 },
      { header: 'Address', key: 'address', width: 40 },
      { header: 'Status', key: 'status', width: 15 },
      { header: 'Contractor Name', key: 'contractorName', width: 25 },
      { header: 'Contractor Email', key: 'contractorEmail', width: 30 },
      { header: 'Union Type', key: 'unionType', width: 15 },
      { header: 'Is Signatory', key: 'isSignatory', width: 15 },
      { header: 'Jobsite Progress', key: 'jobSiteProgress', width: 20 },
      { header: 'Start Date', key: 'startDate', width: 15 },
      { header: 'Total Workers', key: 'totalWorkers', width: 15 },
      { header: 'Last Updated', key: 'lastUpdated', width: 15 },
      { header: 'Notes/Comments', key: 'notes', width: 40 }
    ];
  } else {
    // Default for Jobsite Progress Report
    return [
      { header: 'Jobsite Name', key: 'jobSiteName', width: 30 },
      { header: 'Sector', key: 'sector', width: 15 },
      { header: 'Province', key: 'province', width: 15 },
      { header: 'Address', key: 'address', width: 40 },
      { header: 'Status', key: 'status', width: 15 },
      { header: 'Contractor Name', key: 'contractorName', width: 25 },
      { header: 'Contractor Email', key: 'contractorEmail', width: 30 },
      { header: 'Union Type', key: 'unionType', width: 15 },
      { header: 'Is Signatory', key: 'isSignatory', width: 15 },
      { header: 'Jobsite Progress', key: 'jobSiteProgress', width: 20 },
      { header: 'Start Date', key: 'startDate', width: 15 },
      { header: 'Last Updated', key: 'lastUpdated', width: 15 },
      { header: 'Notes/Comments', key: 'notes', width: 40 }
    ];
  }
};

export const exportToExcel = async (reportData: any[], reportType: string): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Report Data');

  // Get the current columns configuration based on report type
  const currentColumns = getExcelColumns(reportType);

  // Set the columns
  worksheet.columns = currentColumns;

  // Style the header row
  worksheet.getRow(1).font = { bold: true };
  worksheet.getRow(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' }
  };

  // Add data rows with proper formatting
  reportData.forEach(row => {
    const formattedRow: any = {};
    currentColumns.forEach(col => {
      if (col.key === 'address' && row[col.key]) {
        formattedRow[col.key] = `${row[col.key].address1}, ${row[col.key].city}, ${row[col.key].postalCode}`;
      } else if (col.key === 'notes' && Array.isArray(row[col.key])) {
        formattedRow[col.key] = row[col.key].join('; ');
      } else if (['startDate', 'endDate', 'lastUpdated', 'dateOfVisit', 'visitDate'].includes(col.key) && row[col.key]) {
        formattedRow[col.key] = new Date(row[col.key]).toLocaleDateString('en-US', { timeZone: 'UTC' });
      } else {
        formattedRow[col.key] = row[col.key] || '';
      }
    });
    worksheet.addRow(formattedRow);
  });

  // Style status cells (only for non-Monthly Organizer Activity Report)
  if (reportType !== 'Monthly Organizer Activity Report') {
    const statusColumnIndex = currentColumns.findIndex(col => col.key === 'status') + 1;
    if (statusColumnIndex > 0) {
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // Skip header row
        const statusCell = row.getCell(statusColumnIndex);
        if (statusCell.value?.toString().toLowerCase() === 'active') {
          statusCell.font = { color: { argb: 'FF45BE37' } }; // Green
        } else {
          statusCell.font = { color: { argb: 'FFBE3737' } }; // Red
        }
      });
    }
  }

  // Generate and download the file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  // Format current date for filename
  const currentDate = new Date().toISOString().split('T')[0];
  
  // Create filename based on report type
  const filename = `${reportType.replace(/\s+/g, '_')}_${currentDate}.xlsx`;
  
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};
